import { useRouterState } from "@tanstack/react-router";
import { createContext, type ReactNode, use, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/shared/auth/auth.context";
import {
  buildNavCommands,
  buildWorkspaceCommands,
  findCommandByHref,
} from "@/shared/components/command-menu/command-items";
import type { CommandEntry } from "@/shared/components/command-menu/command-match";
import {
  type RecentEntry,
  useRecentCommands,
} from "@/shared/components/command-menu/use-recent-commands";
import { useEntitlements } from "@/shared/entitlements/entitlements.context";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";

type CommandMenuContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Every destination the caller can reach, already gated and slug-resolved. */
  navCommands: CommandEntry[];
  /** Switch commands for the caller's other workspaces. Empty when they have one. */
  workspaceCommands: CommandEntry[];
  recents: RecentEntry[];
  record: (entry: RecentEntry) => void;
  /** Current pathname, so the menu can drop the page you are already on. */
  currentPathname: string;
};

const CommandMenuContext = createContext<CommandMenuContextValue | null>(null);

/// Owns the command menu's open state, the global shortcut, the gated command
/// list, and the recents recorder. Both the sidebar trigger and the dialog
/// need all four, so deriving them once here keeps the two sides from
/// disagreeing. Mounted inside the router (next to ActiveWorkspaceProvider)
/// because it reads the URL and the active workspace.
export function CommandMenuProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const auth = useAuth();
  const { features } = useEntitlements();
  const { workspace, workspaces } = useActiveWorkspace();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { recents, record } = useRecentCommands(workspace.id);

  // Cmd+K on macOS, Ctrl+K elsewhere. Cmd+\ is already the sidebar collapse
  // (use-sidebar-collapsed.tsx); Cmd+K was unclaimed.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((previous) => !previous);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const navCommands = useMemo(() => {
    const workspaceRoles = auth.memberships
      .filter((membership) => membership.workspaceId === workspace.id)
      .map((membership) => membership.role);
    return buildNavCommands({
      accountRole: auth.user?.accountRole ?? null,
      workspaceRoles,
      features,
      workspaceSlug: workspace.slug,
    });
  }, [auth.memberships, auth.user?.accountRole, features, workspace.id, workspace.slug]);

  const workspaceCommands = useMemo(
    () => buildWorkspaceCommands({ workspaces, activeWorkspaceId: workspace.id }),
    [workspaces, workspace.id],
  );

  // Recents follow real navigation, not only menu selections, so clicking a
  // sidebar link feeds the list too and it is useful sooner. Pathnames that
  // match no command (detail pages like /acme/goals/abc123) are ignored
  // rather than recorded under a fabricated label.
  useEffect(() => {
    const visited = findCommandByHref(navCommands, pathname);
    if (!visited) return;
    record({ id: visited.id, label: visited.label, href: visited.href });
  }, [pathname, navCommands, record]);

  const value = useMemo<CommandMenuContextValue>(
    () => ({
      open,
      setOpen,
      navCommands,
      workspaceCommands,
      recents,
      record,
      currentPathname: pathname,
    }),
    [open, navCommands, workspaceCommands, recents, record, pathname],
  );

  return <CommandMenuContext value={value}>{children}</CommandMenuContext>;
}

export function useCommandMenu(): CommandMenuContextValue {
  const context = use(CommandMenuContext);
  if (context === null) {
    throw new Error("useCommandMenu must be used inside a <CommandMenuProvider>");
  }
  return context;
}
